import React, { useContext, useEffect } from 'react'
import './App.css'
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom'
import ReactGA from 'react-ga'
import Toast from 'utils/toast/Toast'

// Utils + constants
import storage from 'utils/storage'
import { checkTokenDate } from 'utils/token'
import { storageKey } from 'constants/storage'

// Contexts
import { MovementsProvider } from 'contexts/movements'
import { UserProvider, UserContext } from 'contexts/user'
import { MeProvider } from 'contexts/me'

// -------  PAGES --------
import SignIn from 'pages/signin'
import Pending from 'pages/pendiente/Pending'
import RejectedAudit from 'pages/rejectedaudit'
import Home from 'pages/home'
// help center
import FAQ from 'pages/helpCenter/faq'
import Legals from 'pages/helpCenter/Legals'
import AdhesionContracts from 'pages/helpCenter/AdhesionContracts'
import ConsumerInformation from 'pages/helpCenter/ConsumerInformation'
import ContactUs from 'pages/helpCenter/ContactUs'
// advances
import AdvanceOTPValidation from 'pages/advances/AdvanceOTP'
import WayniCredit from 'pages/advances'
import AdvanceTypeOptions from 'pages/advances/AdvanceTypeOptions'
import ConfirmRequestAdvance from 'pages/advances/ConfirmRequestAdvance'
import FinishAdvance from 'pages/advances/RequestAdvanceResult'
import HelpCenter from 'pages/helpCenter'
import LoanDetail from 'pages/advances/LoanDetail'
import RequestAdvance from 'pages/advances/RequestAdvance'
import RequestZeroRateAdvance from 'pages/advances/RequestZeroRateAdvance'
import ScoringError from 'pages/advances/ScoringError'
// transfers + movements
import CashoutError from 'pages/cashout/CashoutError'
import CashoutSuccess from 'pages/cashout/CashoutSuccess'
import CashoutOTP from 'pages/cashout/CashoutOTP'
import ConfirmTransfer from 'pages/cashout/ConfirmTransfer'
import Contact from 'pages/cashout/Contact'
import Contacts from 'pages/cashout/Contacts'
import MobileActivityFilter from 'pages/mobilefilter'
import Movements from 'pages/movements'
import MovementsViewDetail from 'pages/movementViewDetail'
import NewAccount from 'pages/cashout/NewAccount'
import NewAccountError from 'pages/cashout/NewAccountError'
import NewTransfer from 'pages/cashout/NewTransfer'
import Recipient from 'pages/cashout/Recipient'
import Transfers from 'pages/cashout'
import TransferActivityDetailPage from 'pages/cashout/TransferActivityDetailPage'
// errors
import DefaultError from 'pages/errors/DefaultError'
import AdvanceError from 'pages/errors/AdvanceError'
// discounts | benefits
import Discount from 'pages/discounts'
// cards
import ActivatePhysicalCard from 'pages/ActivatePhysicalCard'
import MyCard from 'pages/myCard'
import WayniCardSuccess from 'pages/wayniCardSuccess'
import CardDataOTP from 'pages/myCard/CardDataOTP'
import WayniCardAddress from 'pages/wayniCardAddress'
// credit score
import Capacity from 'pages/creditProfile/capacity'
import CreditScore from 'pages/creditProfile'
import LineDetail from 'pages/creditProfile/lineDetail'
import TableDebtsInstitution from 'pages/creditProfile/institutions'
// hub
import Membership from 'pages/membership'
import SubscriptionResult from 'components/SubscriptionResult'
// others
import TermsConditions from 'pages/termsConditions'
import LoginOTP from 'pages/loginOTP'
import PasswordRecovery from 'pages/passwordRecovery'
import RecoveryPasswordMethod from 'components/RecoveryPasswordMethod'
import ChangePassword from 'pages/changePassword'
import RecoveryPwdResults from 'components/RecoveryPwdResults'
import OTPChangePassword from 'pages/loginChangePassword'
import OTPError from 'pages/OTPError'
import ExtraCash from 'pages/extracash'
import Money from 'pages/money'
import Cvu from 'pages/cvu'
import Configuration from 'pages/configuration'
import MyProfile from 'pages/myProfile'
import Alias from 'pages/alias'

import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { CASH_OUT_MAINTENANCE } from 'config'
import UnavailableCashout from 'pages/cashout/UnavailableCashout'
import WayniCardUpdateAddress from 'pages/wayniCardUpdateAddress'

function RequireAuth({ children }) {
  const location = useLocation()
  const isExpired = checkTokenDate()
  const { logout, refreshUserToken } = useContext(UserContext)

  useEffect(() => {
    if (isExpired) {
      refreshUserToken()
    }
  }, [isExpired])

  const token = storage.get(storageKey.USER)?.access_token

  if (!token) {
    logout()
    return <Navigate to='/' state={{ from: location }} replace />
  }

  return children
}

function App() {
  useEffect(() => {
    ReactGA.pageview(window.location.pathname + window.location.search)

    // GET UTM
    const queryObject = {
      referrer: document.referrer,
      utm_campaign: new URLSearchParams(window.location.search).get(
        'utm_campaign'
      ),
      utm_content: new URLSearchParams(window.location.search).get(
        'utm_content'
      ),
      utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
      utm_source: new URLSearchParams(window.location.search).get('utm_source'),
      utm_term: new URLSearchParams(window.location.search).get('utm_term')
    }
    const filteredQuery = Object.fromEntries(
      Object.entries(queryObject).filter(
        ([, value]) => value != null && value !== ''
      )
    )
    sessionStorage.setItem('UTM', JSON.stringify(filteredQuery))
  }, [])

  return (
    <BrowserRouter>
      <UserProvider>
        <MeProvider>
          <MovementsProvider>
            <Routes>
              {/* public routes   */}
              <Route path='/' exact element={<SignIn />} />
              <Route path='ingresando' element={<LoginOTP />} />
              <Route path='contrasena-token' element={<OTPChangePassword />} />
              <Route path='cuenta-rechazada' element={<RejectedAudit />} />
              {/*  private routes */}
              <Route
                path='pendiente'
                element={
                  <RequireAuth>
                    <Pending />
                  </RequireAuth>
                }
              />
              <Route
                path='home'
                element={
                  <RequireAuth>
                    <Home />
                  </RequireAuth>
                }
              />
              <Route
                path='mis-tarjetas'
                exact
                element={
                  <RequireAuth>
                    <MyCard />
                  </RequireAuth>
                }
              />
              <Route
                path='mis-tarjetas/mostrar-datos/:type'
                element={
                  <RequireAuth>
                    <CardDataOTP />
                  </RequireAuth>
                }
              />
              <Route
                path='mis-datos'
                element={
                  <RequireAuth>
                    <MyProfile />
                  </RequireAuth>
                }
              />
              <Route
                path='cargar-dinero'
                element={
                  <RequireAuth>
                    <Money />
                  </RequireAuth>
                }
              />
              <Route
                path='modificar-alias'
                element={
                  <RequireAuth>
                    <Alias />
                  </RequireAuth>
                }
              />
              <Route
                path='cvu'
                element={
                  <RequireAuth>
                    <Cvu />
                  </RequireAuth>
                }
              />
              <Route
                path='movimientos'
                element={
                  <RequireAuth>
                    <Movements />
                  </RequireAuth>
                }
              />
              <Route
                exact
                path='transferencias'
                element={
                  <RequireAuth>
                    {CASH_OUT_MAINTENANCE ? (
                      <UnavailableCashout />
                    ) : (
                      <Transfers />
                    )}
                  </RequireAuth>
                }
              />
              <Route
                path='configuracion'
                element={
                  <RequireAuth>
                    <Configuration />
                  </RequireAuth>
                }
              />
              <Route
                path='recuperar-contrasena'
                element={
                  <RequireAuth>
                    <PasswordRecovery />
                  </RequireAuth>
                }
              />
              <Route
                path='recuperar-contrasena/:method'
                element={
                  <RequireAuth>
                    <RecoveryPasswordMethod />
                  </RequireAuth>
                }
              />
              <Route
                path='cambiar-contrasena/:method'
                element={
                  <RequireAuth>
                    <RecoveryPwdResults />
                  </RequireAuth>
                }
              />
              <Route
                path='cambiar-contrasena'
                element={
                  <RequireAuth>
                    <ChangePassword />
                  </RequireAuth>
                }
              />
              <Route
                path='ver-detalle-transferencia'
                element={
                  <RequireAuth>
                    <TransferActivityDetailPage />
                  </RequireAuth>
                }
              />
              <Route
                path='ver-detalle-movimientos'
                element={
                  <RequireAuth>
                    <MovementsViewDetail />
                  </RequireAuth>
                }
              />
              <Route
                path='filtrar-movimientos'
                element={
                  <RequireAuth>
                    <MobileActivityFilter />
                  </RequireAuth>
                }
              />
              <Route
                path='terminos-y-condiciones'
                element={
                  <RequireAuth>
                    <TermsConditions />
                  </RequireAuth>
                }
              />
              <Route
                exact
                path='wayni-credito'
                element={
                  <RequireAuth>
                    <WayniCredit />
                  </RequireAuth>
                }
              />
              <Route
                path='adelantos/tipo-de-adelanto'
                element={
                  <RequireAuth>
                    <AdvanceTypeOptions />
                  </RequireAuth>
                }
              />
              <Route
                path='wayni-credito/detalle'
                element={
                  <RequireAuth>
                    <LoanDetail />
                  </RequireAuth>
                }
              />
              <Route
                exact
                path='pedir-adelanto'
                element={
                  <RequireAuth>
                    <RequestAdvance />
                  </RequireAuth>
                }
              />
              <Route
                exact
                path='pedir-adelanto/sin-interes'
                element={
                  <RequireAuth>
                    <RequestZeroRateAdvance />
                  </RequireAuth>
                }
              />
              <Route
                path='pedir-adelanto/confirmar'
                element={
                  <RequireAuth>
                    <ConfirmRequestAdvance />
                  </RequireAuth>
                }
              />
              <Route
                path='pedir-adelanto/validar'
                element={
                  <RequireAuth>
                    <AdvanceOTPValidation />
                  </RequireAuth>
                }
              />
              <Route
                path='pedir-adelanto/error-otp'
                element={
                  <RequireAuth>
                    <OTPError />
                  </RequireAuth>
                }
              />
              <Route
                exact
                path='error-adelanto'
                element={
                  <RequireAuth>
                    <AdvanceError />
                  </RequireAuth>
                }
              />
              <Route
                path='error-adelanto/:result'
                element={
                  <RequireAuth>
                    <ScoringError />
                  </RequireAuth>
                }
              />
              <Route
                path='fin-adelanto'
                element={
                  <RequireAuth>
                    <FinishAdvance />
                  </RequireAuth>
                }
              />
              <Route
                path='direccion-tarjeta-fisica'
                element={
                  <RequireAuth>
                    <WayniCardAddress />
                  </RequireAuth>
                }
              />
              <Route
                path='actualizar-direccion-tarjeta-fisica'
                element={
                  <RequireAuth>
                    <WayniCardUpdateAddress />
                  </RequireAuth>
                }
              />
              <Route
                path='seguimiento-tarjeta-fisica'
                element={
                  <RequireAuth>
                    <WayniCardSuccess />
                  </RequireAuth>
                }
              />
              <Route
                path='activar-tarjeta-fisica'
                element={
                  <RequireAuth>
                    <ActivatePhysicalCard />
                  </RequireAuth>
                }
              />
              <Route
                path='error'
                element={
                  <RequireAuth>
                    <DefaultError />
                  </RequireAuth>
                }
              />
              <Route
                exact
                path='suscripcion/:origin?'
                element={
                  <RequireAuth>
                    <Membership />
                  </RequireAuth>
                }
              />
              <Route
                path='suscripcion/:id'
                element={
                  <RequireAuth>
                    <SubscriptionResult />
                  </RequireAuth>
                }
              />
              <Route
                path='extraer-dinero'
                element={
                  <RequireAuth>
                    <ExtraCash />
                  </RequireAuth>
                }
              />
              <Route
                path='promociones/detalle'
                exact
                element={
                  <RequireAuth>
                    <Discount />
                  </RequireAuth>
                }
              />
              <Route
                path='transferencias/contactos'
                element={
                  <RequireAuth>
                    {CASH_OUT_MAINTENANCE ? (
                      <UnavailableCashout />
                    ) : (
                      <Contacts />
                    )}
                  </RequireAuth>
                }
              />
              <Route
                path='transferencias/contacto/:own?'
                element={
                  <RequireAuth>
                    {CASH_OUT_MAINTENANCE ? (
                      <UnavailableCashout />
                    ) : (
                      <Contact />
                    )}
                  </RequireAuth>
                }
              />
              <Route
                path='transferencias/nueva-cuenta'
                element={
                  <RequireAuth>
                    {CASH_OUT_MAINTENANCE ? (
                      <UnavailableCashout />
                    ) : (
                      <NewAccount />
                    )}
                  </RequireAuth>
                }
              />
              <Route
                path='transferencias/error-nueva-cuenta'
                element={
                  <RequireAuth>
                    <NewAccountError />
                  </RequireAuth>
                }
              />
              <Route
                path='transferencias/destinatario'
                element={
                  <RequireAuth>
                    {CASH_OUT_MAINTENANCE ? (
                      <UnavailableCashout />
                    ) : (
                      <Recipient />
                    )}
                  </RequireAuth>
                }
              />
              <Route
                path='transferencias/nueva-transferencia'
                element={
                  <RequireAuth>
                    {CASH_OUT_MAINTENANCE ? (
                      <UnavailableCashout />
                    ) : (
                      <NewTransfer />
                    )}
                  </RequireAuth>
                }
              />
              <Route
                path='transferencias/confirmar-transferencia'
                element={
                  <RequireAuth>
                    {CASH_OUT_MAINTENANCE ? (
                      <UnavailableCashout />
                    ) : (
                      <ConfirmTransfer />
                    )}
                  </RequireAuth>
                }
              />
              <Route
                path='transferencias/transferencia-exitosa'
                element={
                  <RequireAuth>
                    <CashoutSuccess />
                  </RequireAuth>
                }
              />
              <Route
                path='transferencias/error'
                element={
                  <RequireAuth>
                    <CashoutError />
                  </RequireAuth>
                }
              />
              <Route
                path='transferencias/validar'
                element={
                  <RequireAuth>
                    {CASH_OUT_MAINTENANCE ? (
                      <UnavailableCashout />
                    ) : (
                      <CashoutOTP />
                    )}
                  </RequireAuth>
                }
              />
              <Route
                path='perfil-crediticio'
                exact
                element={
                  <RequireAuth>
                    <CreditScore />
                  </RequireAuth>
                }
              />
              <Route
                path='perfil-crediticio/capacidad-de-pago'
                element={
                  <RequireAuth>
                    <Capacity />
                  </RequireAuth>
                }
              />
              <Route
                path='perfil-crediticio/info-por-institucion'
                element={
                  <RequireAuth>
                    <TableDebtsInstitution />
                  </RequireAuth>
                }
              />
              <Route
                path='perfil-crediticio/detalle-de-linea/:type'
                element={
                  <RequireAuth>
                    <LineDetail />
                  </RequireAuth>
                }
              />
              <Route
                exact
                path='centro-de-ayuda'
                element={
                  <RequireAuth>
                    <HelpCenter />
                  </RequireAuth>
                }
              />
              <Route
                path='centro-de-ayuda/preguntas-frecuentes'
                element={
                  <RequireAuth>
                    <FAQ />
                  </RequireAuth>
                }
              />
              <Route
                exact
                path='centro-de-ayuda/legales'
                element={
                  <RequireAuth>
                    <Legals />
                  </RequireAuth>
                }
              />
              <Route
                path='centro-de-ayuda/legales/contratos-de-adhesion'
                element={
                  <RequireAuth>
                    <AdhesionContracts />
                  </RequireAuth>
                }
              />
              <Route
                path='centro-de-ayuda/informacion-al-consumidor'
                element={
                  <RequireAuth>
                    <ConsumerInformation />
                  </RequireAuth>
                }
              />
              <Route
                path='centro-de-ayuda/contactanos'
                element={
                  <RequireAuth>
                    <ContactUs />
                  </RequireAuth>
                }
              />
            </Routes>
            <Toast />
          </MovementsProvider>
        </MeProvider>
      </UserProvider>
    </BrowserRouter>
  )
}

export default App
